import { Component, ChangeDetectionStrategy, input, output, computed, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  IdentityScoreData,
  ExecutiveMetrics,
  ScoreSeverityTier,
} from '../../../../services/identity-security.service';
import { ScoreDetailsDialogComponent } from './components/score-details-dialog/score-details-dialog.component';

export type HeroKpiKey = 'high-risk' | 'privileged-at-risk' | 'leaked-credentials' | 'monitored-users';

@Component({
  selector: 'app-hero-banner',
  standalone: true,
  imports: [CommonModule, ScoreDetailsDialogComponent],
  templateUrl: './hero-banner.component.html',
  styleUrl: './hero-banner.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class HeroBannerComponent {
  identityScore = input<IdentityScoreData | null>(null);
  executiveMetrics = input<ExecutiveMetrics | null>(null);
  isLoading = input<boolean>(true);
  /** Re-emitted from the score dialog when a customer reassigns a check's tier. */
  tierChange = output<{ checkId: string; severity: ScoreSeverityTier }>();
  /** Customer clicked a KPI tile; parent opens the entity dialog. */
  openKpiDetail = output<HeroKpiKey>();

  public showScoreDetails = signal(false);

  /**
   * Risk band for the score (100 = best), using the same tier vocabulary as the checks:
   * Critical 0 to 24, High 25 to 49, Medium 50 to 74, Low 75 to 100.
   */
  public scoreBand = computed<ScoreSeverityTier>(() => {
    const score = this.identityScore()?.current_score ?? 0;
    if (score >= 75) return 'Low';
    if (score >= 50) return 'Medium';
    if (score >= 25) return 'High';
    return 'Critical';
  });

  /** Shared severity chip, the same one the checks use. */
  public scoreBadgeClass = computed(() => `severity-badge severity-${this.scoreBand().toLowerCase()}`);

  /**
   * Tokens rather than hex, returned as `var(...)` because this feeds a plain
   * `[style.background-color]` binding. High borrows the severity chip's orange, since the
   * status palette has no step between warning and danger.
   */
  public scoreColor = computed(() => {
    const color: Record<ScoreSeverityTier, string> = {
      Critical: 'var(--color-danger)',
      High: 'var(--severity-high-fg)',
      Medium: 'var(--color-warning)',
      Low: 'var(--color-success)',
    };
    return color[this.scoreBand()];
  });

  /** True when the leaked-credentials source can't be trusted; tile becomes a non-clickable "Data Unavailable" state. */
  public leakedCredentialsUnavailable = computed(() => {
    const source = this.executiveMetrics()?.leaked_credentials_source;
    return !source || source === 'unavailable';
  });

  /** Sub-context shown under the tile to explain why the count is missing. */
  public leakedCredentialsContext = computed(() => {
    const source = this.executiveMetrics()?.leaked_credentials_source;
    if (!source || source === 'unavailable') return 'Data unavailable';
    return 'Unrotated since detection';
  });

  /**
   * The one line that says what the score was computed from.
   *
   * Three segments, each shown only when it has something to say:
   *   Based on 10 security checks
   *   Based on 10 security checks • 1 risk accepted
   *   Based on 9 of 10 security checks • 1 risk accepted • 1 not evaluated
   */
  public scoreCheckSummary = computed(() => {
    const s = this.identityScore();
    if (!s) return '';

    // Accepted checks are a sub-state of failed, so they already sit inside
    // `failing_checks` and count as evaluated.
    const evaluated = s.passing_checks + s.failing_checks;
    // `disabled_checks` is the API's name for not-evaluated; its serializer documents the
    // rename. Read rather than derived from `total - evaluated` so the count in the segment
    // and the count driving the "X of Y" form cannot disagree with each other.
    const notEvaluated = s.disabled_checks;

    const segments: string[] = [
      // The adjusted form whenever something could not be evaluated, which is the normal case
      // rather than an edge: most production tenants cannot run between one and eight of the
      // ten checks. Without it the customer reads "Based on 9 security checks" and has no way
      // to tell the catalog has ten.
      notEvaluated > 0
        ? `Based on ${evaluated} of ${s.total_checks} security checks`
        : `Based on ${evaluated} security checks`,
    ];

    if (s.accepted_checks > 0) {
      segments.push(`${s.accepted_checks} risk${s.accepted_checks === 1 ? '' : 's'} accepted`);
    }
    if (notEvaluated > 0) {
      segments.push(`${notEvaluated} not evaluated`);
    }

    return segments.join(' • ');
  });
}
